import type { WebediaPlayer } from '@webedia-services/webedia-player';

import { urlParts } from 'common/tools/network/url';

import type { PlayerCustomData } from 'website/module/player';

export const NORMALIZED_VIDEO_PUB_FORMAT: Record<string, string> = {
  ac_emergence_svod_footer: 'player_footer_svod',
  ac_emergence_svod_list: 'player_list_svod',
  ac_footer: 'player_footer',
  ac_list: 'player_list',
  booster: 'booster',
  interstitial: 'interstitial',
  player_header: 'player_header'
};

export const CATEGORY_VIDEO_CONTENT = 'video-content';
export const CATEGORY_VIDEO_POSTROLL = 'video-postroll';

export const CATEGORY_VIDEO_PUB_ALLOCINE_NATIVE = 'video-pub-allocine-native';

/*
Build a context object to compute tracking information
The context object contains:
- the merge of customData and video data
- some player functions

This function was created to make the tracking work with multiple videos. The
metadata (trackingId, title, ...) was saved on the player object. With multiple
videos, it's no longer possible. This functions mimics the "one video player"
situation by merging the "player level" metadata with the currently playing
video metadata.
*/
export const getCurrentCustomData = (
  customData: PlayerCustomData,
  webediaPlayer: WebediaPlayer
) => {
  const videos = customData.videos ?? [];
  const currentVideo = webediaPlayer.getPlaylistItem();

  let videoIndex = 0;

  if (currentVideo) {
    videoIndex = videos.findIndex(video => {
      if (currentVideo.mediaid) {
        return video.idDailymotion === currentVideo.mediaid;
      }
      if (currentVideo.title) {
        return video.title === currentVideo.title;
      }
      return false;
    });
  }
  return {
    ...customData,
    ...(videos[videoIndex] ?? {})
  };
};

export const getVideoDisplayHostname = (customData: PlayerCustomData) =>
  customData.context === 'iblogvision' && document.referrer
    ? urlParts(document.referrer).host
    : window.location.hostname;

export const retreiveEntityId = (trackingIdentifier?: string) => {
  if (typeof trackingIdentifier === 'string') {
    const matches = trackingIdentifier.match(/^(\d+)-.*/);
    if (matches !== null && matches.length > 1) {
      return matches[1];
    }
  }

  return '';
};

export const mainContentCategorySelector = (
  webediaPlayer: WebediaPlayer,
  customData: PlayerCustomData
) => {
  const postRollActive = webediaPlayer.postroll?.isPlayingPostrollVideo();
  const campaign = customData.campaign;
  const partnerName = customData.partner?.name;

  if (postRollActive) {
    return CATEGORY_VIDEO_POSTROLL;
  }
  // Player footer is a partner while booster, interstitial, ... are campaigns
  if (
    Object.prototype.hasOwnProperty.call(
      NORMALIZED_VIDEO_PUB_FORMAT,
      campaign ?? partnerName ?? ''
    )
  ) {
    return CATEGORY_VIDEO_PUB_ALLOCINE_NATIVE;
  }
  return CATEGORY_VIDEO_CONTENT;
};

export const getVideoPubFormatValue = (customData: PlayerCustomData) => {
  const campaign = customData.campaign;
  const partnerName = customData.partner?.name;
  const value = campaign ?? partnerName; // Player footer is a partner while booster and intersitial are campaigns
  if (value) {
    for (const index in NORMALIZED_VIDEO_PUB_FORMAT) {
      if (
        Object.prototype.hasOwnProperty.call(NORMALIZED_VIDEO_PUB_FORMAT, index)
      ) {
        if (value.indexOf(index) !== -1) {
          return NORMALIZED_VIDEO_PUB_FORMAT[index];
        }
      }
    }
  }

  return '';
};
